import type {
  OrganizationContextResponse,
  OrganizationCreateInput,
  OrganizationCreationResponse,
  OrganizationListItem,
  OrganizationOnboardingPatchInput,
  OrganizationOnboardingRun,
  OrganizationProvisioningCreateInput,
  OrganizationProvisioningCreateResponse,
  OrganizationProvisioningPatchInput,
  OrganizationProvisioningSummary,
  PublicBranding,
  PublicProviderListing,
  PublicProviderProfile,
  PublicProviderProfileDraft
} from "@kuroo/contracts";
import { KurooApiError } from "./errors";

export type SdkOrganizationContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
  setOrganizationId: (organizationId?: string) => void;
  getOrganizationId: () => string | undefined;
};

export function organizations(context: SdkOrganizationContext): Promise<OrganizationListItem[]> {
  return context.request<OrganizationListItem[]>("/api/v1/organizations");
}

export function organization(context: SdkOrganizationContext, organizationId: string): Promise<OrganizationListItem> {
  return context.request<OrganizationListItem>(`/api/v1/organizations/${encodeURIComponent(organizationId)}`);
}

export function organizationContext(context: SdkOrganizationContext): Promise<OrganizationContextResponse> {
  return context.request<OrganizationContextResponse>("/api/v1/org-context");
}

export async function switchOrganization(context: SdkOrganizationContext, organizationId: string): Promise<OrganizationContextResponse> {
  const result = await context.request<OrganizationContextResponse>(`/api/v1/organizations/${encodeURIComponent(organizationId)}/switch`, { method: "POST", body: "{}" });
  context.setOrganizationId(result.context.organizationId);
  return result;
}

export function createOrganization(context: SdkOrganizationContext, input: OrganizationCreateInput): Promise<OrganizationCreationResponse> {
  return context.request<OrganizationCreationResponse>("/api/v1/organizations", { method: "POST", body: JSON.stringify(input) });
}

export function provisioningOrganizations(context: SdkOrganizationContext): Promise<{ organizations: OrganizationProvisioningSummary[]; operator: { email: string; source: string } }> {
  return context.request<{ organizations: OrganizationProvisioningSummary[]; operator: { email: string; source: string } }>("/api/v1/internal/provisioning/organizations");
}

export function createProvisioningOrganization(context: SdkOrganizationContext, input: OrganizationProvisioningCreateInput): Promise<OrganizationProvisioningCreateResponse> {
  const idempotencyKey = input.idempotencyKey?.trim() || `provisioning-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`}`;
  return context.request<OrganizationProvisioningCreateResponse>("/api/v1/internal/provisioning/organizations", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": idempotencyKey } });
}

export function updateProvisioningOrganization(context: SdkOrganizationContext, organizationId: string, input: OrganizationProvisioningPatchInput): Promise<{ organization: OrganizationProvisioningSummary; readiness: Record<string, boolean> | null }> {
  return context.request<{ organization: OrganizationProvisioningSummary; readiness: Record<string, boolean> | null }>(`/api/v1/internal/provisioning/organizations/${encodeURIComponent(organizationId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function organizationOnboarding(context: SdkOrganizationContext, organizationId: string | undefined = context.getOrganizationId(), runId?: string): Promise<OrganizationOnboardingRun> {
  const id = organizationId?.trim();
  if (!id) throw new KurooApiError("ORG_CONTEXT_REQUIRED", "Seleccioná una organización para consultar el onboarding.");
  const suffix = runId ? `/${encodeURIComponent(runId)}` : "";
  return context.request<OrganizationOnboardingRun>(`/api/v1/organizations/${encodeURIComponent(id)}/onboarding${suffix}`);
}

export function updateOrganizationOnboarding(context: SdkOrganizationContext, input: OrganizationOnboardingPatchInput, organizationId: string | undefined = context.getOrganizationId(), runId?: string): Promise<OrganizationOnboardingRun> {
  const id = organizationId?.trim();
  if (!id) throw new KurooApiError("ORG_CONTEXT_REQUIRED", "Seleccioná una organización para guardar el onboarding.");
  const suffix = runId ? `/${encodeURIComponent(runId)}` : "";
  return context.request<OrganizationOnboardingRun>(`/api/v1/organizations/${encodeURIComponent(id)}/onboarding${suffix}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function publicConfig(context: SdkOrganizationContext, options: { organizationSlug?: string; signal?: AbortSignal } = {}): Promise<PublicBranding> {
  const query = new URLSearchParams();
  if (options.organizationSlug) query.set("organizationSlug", options.organizationSlug);
  return context.request<PublicBranding>(`/api/v1/config/public${query.size ? `?${query}` : ""}`, { signal: options.signal });
}

export function publicProviders(context: SdkOrganizationContext, options: { serviceId?: string; locationId?: string; search?: string; organizationSlug?: string; signal?: AbortSignal } = {}): Promise<PublicProviderListing[]> {
  const query = new URLSearchParams();
  if (options.serviceId) query.set("serviceId", options.serviceId);
  if (options.locationId) query.set("locationId", options.locationId);
  if (options.search) query.set("search", options.search);
  if (options.organizationSlug) query.set("organizationSlug", options.organizationSlug);
  return context.request<PublicProviderListing[]>(`/api/v1/public/providers${query.size ? `?${query}` : ""}`, { signal: options.signal });
}

export function publicProvider(context: SdkOrganizationContext, slug: string, options: { organizationSlug?: string; signal?: AbortSignal } = {}): Promise<PublicProviderProfile> {
  const query = new URLSearchParams();
  if (options.organizationSlug) query.set("organizationSlug", options.organizationSlug);
  return context.request<PublicProviderProfile>(`/api/v1/public/providers/${encodeURIComponent(slug)}${query.size ? `?${query}` : ""}`, { signal: options.signal });
}

export function publicProfileDraft(context: SdkOrganizationContext, providerId?: string): Promise<PublicProviderProfileDraft> {
  const suffix = providerId ? `/${encodeURIComponent(providerId)}` : "/me";
  return context.request<PublicProviderProfileDraft>(`/api/v1/public/profile${suffix}`);
}

export function updatePublicProfileDraft(context: SdkOrganizationContext, input: Partial<Omit<PublicProviderProfileDraft, "providerId" | "status" | "pendingEditorial" | "publishedAt" | "updatedAt">> & { providerId?: string }): Promise<PublicProviderProfileDraft> {
  const suffix = input.providerId ? `/${encodeURIComponent(input.providerId)}` : "/me";
  const { providerId: _providerId, ...payload } = input;
  return context.request<PublicProviderProfileDraft>(`/api/v1/public/profile${suffix}`, { method: "PATCH", body: JSON.stringify(payload) });
}

export function publishPublicProfile(context: SdkOrganizationContext, providerId?: string): Promise<PublicProviderProfileDraft> {
  const suffix = providerId ? `/${encodeURIComponent(providerId)}` : "/me";
  return context.request<PublicProviderProfileDraft>(`/api/v1/public/profile${suffix}/publish`, { method: "POST", body: "{}" });
}

export function unpublishPublicProfile(context: SdkOrganizationContext, providerId?: string): Promise<PublicProviderProfileDraft> {
  const suffix = providerId ? `/${encodeURIComponent(providerId)}` : "/me";
  return context.request<PublicProviderProfileDraft>(`/api/v1/public/profile${suffix}/unpublish`, { method: "POST", body: "{}" });
}
